import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

/**
 * Cookiebot exposes its current consent state on the `window.Cookiebot`
 * global as well as in the `CookieConsent` cookie. We read from the global
 * first (most up to date) and fall back to parsing the cookie so the details
 * still render when the global has not yet initialised.
 */
declare global {
  interface Window {
    Cookiebot?: {
      renew?: () => void;
      show?: () => void;
      withdraw?: () => void;
      /** True once the user has actively responded to the consent dialog. */
      hasResponse?: boolean;
      consented?: boolean;
      consentID?: string | number;
      /** ISO-8601 timestamp of when consent was last given. */
      consentUTC?: string;
      /** Domain the consent was recorded against. */
      domain?: string;
      consent?: {
        stamp?: string;
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
        marketing?: boolean;
        method?: string;
      };
    };
  }
}

interface CookieConsentState {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

@Component({
  selector: 'vui-cookie-policy-content',
  templateUrl: './cookie-policy-content.component.html',
  styleUrls: ['./cookie-policy-content.component.scss'],
  standalone: false,
})
export class CookiePolicyContentComponent implements OnInit {
  @Input() cookieDeclarationUrl = '';
  @Input() privacyPolicyLink = '';
  @Output() viewPreferences = new EventEmitter<void>();

  /** True when a Cookiebot consent record is available to display. */
  hasConsentDetails = false;
  consentDomains: string[] = [];
  consentStateLabel = '';
  consentId = '';
  consentDate = '';

  /**
   * Delay before re-reading Cookiebot state after a withdraw, giving its
   * iframe time to persist the updated consent.
   */
  private readonly CONSENT_REFRESH_DELAY_MS = 500;

  ngOnInit(): void {
    this.loadConsentDetails();
  }

  /**
   * Re-reads the Cookiebot consent state so the displayed details stay in sync
   * with the user's latest choice. The component is not recreated when the
   * Cookie Policy modal opens/closes, so the footer calls this on every modal
   * open and close to avoid showing stale consent captured at `ngOnInit`.
   */
  refreshConsentDetails(): void {
    this.loadConsentDetails();
  }

  onViewPreferences(): void {
    this.viewPreferences.emit();
  }

  /**
   * "Change your consent" behaves identically to the "View and change your
   * current cookie preferences" link: it emits to the footer, which closes the
   * Cookie Policy modal and opens the Cookiebot dialog.
   */
  onChangeConsent(): void {
    this.viewPreferences.emit();
  }

  /**
   * Withdraws the user's consent via Cookiebot and refreshes the displayed
   * details. `withdraw()` updates `window.Cookiebot` asynchronously (via its
   * iframe), so we re-read the state after a short delay to reflect the new
   * necessary-only consent. Safe no-op if Cookiebot is not present.
   */
  onWithdrawConsent(): void {
    window.Cookiebot?.withdraw?.();
    // Re-read after a short delay so the details reflect the new necessary-only
    // state. `preserveExisting` avoids momentarily hiding the already-visible
    // block if Cookiebot has not finished persisting the update yet.
    setTimeout(
      () => this.loadConsentDetails(true),
      this.CONSENT_REFRESH_DELAY_MS,
    );
  }

  /**
   * Populates the consent-details view model from the Cookiebot global and the
   * `CookieConsent` cookie. When no consent record exists (e.g. Cookiebot not
   * loaded / no consent yet) the block is hidden via `hasConsentDetails`.
   *
   * @param preserveExisting When true, an already-visible block is left on
   *   screen if a valid consent state cannot be resolved (used for the
   *   post-withdraw refresh so the section does not momentarily disappear).
   */
  private loadConsentDetails(preserveExisting = false): void {
    const cookiebot = window.Cookiebot;
    const cookie = this.parseCookieConsentCookie();
    const state = this.resolveConsentState(cookie);

    // Only show the block once a real consent decision exists. When Cookiebot
    // is loaded we trust its `hasResponse`/`consented` flags; otherwise we rely
    // on the presence of a parsed consent state from the cookie.
    const hasResponded = cookiebot
      ? !!(cookiebot.hasResponse || cookiebot.consented)
      : !!state;

    if (!state || !hasResponded) {
      if (!preserveExisting) {
        this.hasConsentDetails = false;
      }
      return;
    }

    this.consentDomains = [cookiebot?.domain || window.location.hostname];
    this.consentStateLabel = this.buildStateLabel(state);
    this.consentId = String(cookiebot?.consentID ?? cookie?.['stamp'] ?? '');
    this.consentDate = this.resolveConsentDate(cookiebot?.consentUTC, cookie);
    this.hasConsentDetails = true;
  }

  /**
   * Resolves the consent categories, preferring the live Cookiebot global and
   * falling back to the parsed cookie. Returns null when neither is available.
   */
  private resolveConsentState(
    cookie: Record<string, unknown> | null,
  ): CookieConsentState | null {
    const consent = window.Cookiebot?.consent;
    if (consent) {
      return {
        necessary: !!consent.necessary,
        preferences: !!consent.preferences,
        statistics: !!consent.statistics,
        marketing: !!consent.marketing,
      };
    }

    if (cookie) {
      return {
        necessary: this.toBool(cookie['necessary']),
        preferences: this.toBool(cookie['preferences']),
        statistics: this.toBool(cookie['statistics']),
        marketing: this.toBool(cookie['marketing']),
      };
    }

    return null;
  }

  /**
   * Builds the human-readable "current state" label, matching Cookiebot's
   * phrasing, e.g. "Allow selection (Necessary, Preferences, Marketing)." or
   * "Allow all (Necessary, Preferences, Statistics, Marketing)."
   */
  private buildStateLabel(state: CookieConsentState): string {
    const enabled: string[] = [];
    if (state.necessary) {
      enabled.push($localize`Necessary`);
    }
    if (state.preferences) {
      enabled.push($localize`Preferences`);
    }
    if (state.statistics) {
      enabled.push($localize`Statistics`);
    }
    if (state.marketing) {
      enabled.push($localize`Marketing`);
    }

    const categories = enabled.join(', ');
    const allCategories =
      state.necessary && state.preferences && state.statistics && state.marketing;

    const prefix = allCategories ? $localize`Allow all` : $localize`Allow selection`;

    return `${prefix} (${categories}).`;
  }

  /** Reads and URL-decodes the `CookieConsent` cookie into a key/value map. */
  private parseCookieConsentCookie(): Record<string, unknown> | null {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith('CookieConsent='));

    if (!match) {
      return null;
    }

    const raw = decodeURIComponent(match.substring('CookieConsent='.length));

    // Cookiebot stores the value as a pseudo-JSON string with single quotes
    // and unquoted keys, e.g.
    // {stamp:'...',necessary:true,preferences:false,...,utc:1700000000000}
    const normalised = raw
      .replace(/'/g, '"')
      .replace(/([{,])\s*([A-Za-z0-9_]+)\s*:/g, '$1"$2":');

    try {
      return JSON.parse(normalised);
    } catch {
      return null;
    }
  }

  /**
   * Formats the consent date. Prefers Cookiebot's ISO `consentUTC` string and
   * falls back to the cookie's `utc` epoch-ms value. Renders in Cookiebot's
   * style, e.g. "Wednesday, August 5, 2026 at 02:41:26 PM GMT+3".
   */
  private resolveConsentDate(
    consentUTC: string | undefined,
    cookie: Record<string, unknown> | null,
  ): string {
    let date: Date | null = null;

    if (consentUTC) {
      const parsed = new Date(consentUTC);
      if (!Number.isNaN(parsed.getTime())) {
        date = parsed;
      }
    }

    if (!date) {
      const utc = cookie?.['utc'];
      const epoch = typeof utc === 'number' ? utc : Number(utc);
      if (epoch && !Number.isNaN(epoch)) {
        date = new Date(epoch);
      }
    }

    if (!date) {
      return '';
    }

    return this.formatConsentDate(date);
  }

  /**
   * Formats a date as "Wednesday, August 5, 2026 at 02:41:26 PM GMT+3" to
   * mirror Cookiebot's own consent-details rendering. Falls back to the default
   * locale string if Intl formatting is unavailable.
   */
  private formatConsentDate(date: Date): string {
    try {
      return new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  }

  private toBool(value: unknown): boolean {
    return value === true || value === 'true';
  }
}
